import React from 'react';
import { shallow } from 'zustand/shallow';
import useMapStore from '@store/useMapStore';
import { EMPTY_ARRAY } from '@utils/constants';
import { handleTileClick, handleTileHover, handleTileLeave } from '@services/mapService';
import './HighlightLayer.css';

const HighlightLayer = ({ tileSize }) => {
  const highlightTiles = useMapStore((state) => state.visual?.highlightTiles, shallow) || EMPTY_ARRAY;
  const aoePreviewTiles = useMapStore((state) => state.visual?.aoePreviewTiles, shallow) || EMPTY_ARRAY;
  const aoePreviewBeneficial = useMapStore((state) => state.visual?.aoePreviewBeneficial) || false;
  const focusTile = useMapStore((state) => state.focusTile);

  const hasHighlights = Array.isArray(highlightTiles) && highlightTiles.length > 0;
  const hasAoePreview = Array.isArray(aoePreviewTiles) && aoePreviewTiles.length > 0;
  const hasFocusTile = focusTile && typeof focusTile.x === 'number' && typeof focusTile.y === 'number';

  // Debug log for focusTile
  if (focusTile) {
    console.log('[HighlightLayer] focusTile received:', focusTile, 'hasFocusTile:', hasFocusTile, 'tileSize:', tileSize);
  }

  if (!hasHighlights && !hasAoePreview && !hasFocusTile) {
    return null;
  }

  // Create set of AOE tiles for quick lookup (to avoid duplicate rendering)
  const aoeTileSet = new Set(aoePreviewTiles.map(t => `${t.x}-${t.y}`));
  // Determine AOE type class (beneficial = cyan, harmful = orange)
  const aoeTypeClass = aoePreviewBeneficial ? 'aoe-beneficial' : 'aoe-harmful';

  return (
    <div className="map-layer highlight-layer">
      {/* Render targetable tiles (green) */}
      {hasHighlights && highlightTiles.map((tile, index) => {
        if (!tile || typeof tile.x !== 'number' || typeof tile.y !== 'number') {
          console.warn(`[HighlightLayer] Invalid tile at index ${index}:`, tile);
          return null;
        }

        // Check if this tile is also in AOE preview
        const isInAoe = aoeTileSet.has(`${tile.x}-${tile.y}`);

        const style = {
          left: `${tile.x * tileSize}px`,
          top: `${tile.y * tileSize}px`,
          width: `${tileSize}px`,
          height: `${tileSize}px`,
        };

        return (
          <div
            key={`highlight-${tile.x}-${tile.y}-${index}`}
            className={`highlight-tile ${isInAoe ? `in-aoe ${aoeTypeClass}` : ''}`}
            style={style}
            data-type={tile.type || 'targetable'}
            onClick={() => handleTileClick(tile.x, tile.y, null)}
            onMouseEnter={() => handleTileHover(tile.x, tile.y)}
            onMouseLeave={handleTileLeave}
          />
        );
      })}

      {/* Render AOE preview tiles (orange) - only tiles not already rendered as targetable */}
      {hasAoePreview && aoePreviewTiles.map((tile, index) => {
        if (!tile || typeof tile.x !== 'number' || typeof tile.y !== 'number') {
          return null;
        }

        // Skip if already rendered as targetable tile
        const isTargetable = hasHighlights && highlightTiles.some(
          t => t.x === tile.x && t.y === tile.y
        );
        if (isTargetable) {
          return null;
        }

        const style = {
          left: `${tile.x * tileSize}px`,
          top: `${tile.y * tileSize}px`,
          width: `${tileSize}px`,
          height: `${tileSize}px`,
        };

        return (
          <div
            key={`aoe-preview-${tile.x}-${tile.y}-${index}`}
            className={`highlight-tile aoe-preview ${aoeTypeClass}`}
            style={style}
            data-type="aoe-preview"
          />
        );
      })}

      {/* Render focus tile (from UI hover, like CoordinateLink) */}
      {hasFocusTile && (
        <div
          key={`focus-${focusTile.x}-${focusTile.y}`}
          className="highlight-tile focus-tile"
          style={{
            position: 'absolute',
            left: `${focusTile.x * tileSize}px`,
            top: `${focusTile.y * tileSize}px`,
            width: `${tileSize}px`,
            height: `${tileSize}px`,
            // Inline styles to ensure visibility (bypass CSS cache issues)
            backgroundColor: 'transparent',
            outline: '5px solid #FACC15',
            outlineOffset: '-5px',
            zIndex: 100,
            pointerEvents: 'none',
            animation: 'none', // Will add CSS animation later
            boxShadow: '0 0 15px rgba(250, 204, 21, 0.6)',
          }}
          data-type="focus"
          ref={(el) => {
            if (el) {
              console.log('[HighlightLayer] focus-tile DOM rendered:', el);
              console.log('[HighlightLayer] focus-tile computed outline:', window.getComputedStyle(el).outline);
            }
          }}
        />
      )}
    </div>
  );
};

export default React.memo(HighlightLayer);